import { Button, Spinner, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineCalibrateButton } from 'components/machine/buttons/calibrate';
import { MachineUnavailableButton } from 'components/machine/buttons/unavailable';
import { CalibrateBallButton } from 'components/sections/pitch-list/sidebar/calibrate-ball-button';
import { AimingContext } from 'contexts/aiming.context';
import { FiringContext } from 'contexts/firing/context';
import useFiringHeartbeat from 'contexts/firing/useFiringHeartbeat';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { addSeconds } from 'date-fns';
import { FireState, IgnoreAutoFireMode } from 'enums/firing';
import { ProgressDirection } from 'enums/training.enums';
import { useRemoteListener } from 'hooks/machine/useRemoteListener';
import useLatest from 'hooks/useLatestRef';
import usePrevious from 'hooks/usePrevious';
import { t } from 'i18next';
import { TrainPitchesFn } from 'interfaces/function-types';
import { IVerboseComponent } from 'interfaces/i-components';
import { IFireData, IFireResult } from 'interfaces/i-firing';
import { ISendBP, ISendTraining } from 'interfaces/i-pitch-aiming';
import { SfxName } from 'lib_ts/enums/machine-msg.enum';
import { FiringMode } from 'lib_ts/enums/machine.enums';
import { RadixBtnSize } from 'lib_ts/enums/radix-ui';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { throttle } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useDropBallStore, {
  DROP_BALL_COOLDOWN_SEC,
} from 'stores/use-drop-ball-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

const RAGE_CLICK_TIMEOUT = 100;
const RAGE_HOTKEY_TIMEOUT_MS = 500;

type BeforeFireFn = (mode: FiringMode, isReady: boolean) => void;
type AfterFireFn = (result: IFireResult) => void;
type ChangePitchFn = (delta: ProgressDirection) => void;

interface IProps {
  size?: RadixBtnSize;
  className?: string;

  as?: 'dialog-button';

  // only true for pitch list sidebar button so we can render the CalibrateBallButton if needed
  isSidebarControl?: boolean;

  /** provide to perform an action just before fire logic is triggered */
  beforeFire?: BeforeFireFn;

  /** provide to perform an action just after fire logic is triggered */
  afterFire?: AfterFireFn;

  beforeSend?: () => void;

  // whether inputs from any remote control should be
  enableHotkeys?: boolean;

  // how to handle change pitch trigger from remote
  onHotkeyChangePitch?: ChangePitchFn;

  fireData: IFireData;
  sendData: ISendBP | ISendTraining;

  // should be provided while training, ignored while not training
  trainingShots?: IMachineShot[];

  // provided when not in training mode, handles click when pitch is not trained
  onTrainFn?: TrainPitchesFn;
}

export const MachineButton = (props: IProps & IVerboseComponent) => {
  // start the heartbeat to maintain fire state
  useFiringHeartbeat({
    identifier: props.identifier,
    verbose: props.verbose,
    trainingShots: props.trainingShots,
  });

  const calibrated = useMachineStatusStore((s) => s.calibrated);
  const canCalibrate = useMachineStatusStore((s) => s.canCalibrate);
  const r2fReady = useMachineStatusStore((s) => s.r2fReady);

  const { machine, activeModel, openDialog, dropball, playSound, setAutoFire } =
    useContext(MachineContext);

  const { pitchBP, pitchTraining, isTraining } = useContext(AimingContext);

  const droppingBall = useDropBallStore((s) => s.droppingBall);

  const {
    activeButton,
    fireState,
    suspended,
    setFireState,
    safeSetIgnoreAf,
    setLoadingUntil,
    tryFire,
    trySend,
  } = useContext(FiringContext);

  useRemoteListener({
    suspended: !props.enableHotkeys || suspended,
    onPrev: () => throttledChangePitch(ProgressDirection.Prev),
    onNext: () => throttledChangePitch(ProgressDirection.Next),
    onFire: () => {
      if (!activeButton) {
        return;
      }

      switch (activeButton.state) {
        case FireState.ReadyToManualFire: {
          throttledDelayFire();
          return;
        }

        case FireState.LoadPitch: {
          throttledSend();
          return;
        }

        case FireState.DropBall: {
          throttledDropBall();
          return;
        }

        default: {
          // notify the user that their clicker press was ignored
          playSound(SfxName.NOT_READY);
          verboseDebug(
            props,
            `remote > onFire > unexpected state > ${activeButton.state}`
          );
          return;
        }
      }
    },
  });

  useEffect(() => {
    if (isTraining && !props.trainingShots) {
      NotifyHelper.error({
        message_md:
          'Firing context machine button should not be used for training without providing training shots!',
      });
    }
  }, [isTraining, props.trainingShots]);

  const throttledChangePitch = useMemo(
    () =>
      throttle(
        (delta: ProgressDirection) => {
          verboseDebug(props, 'throttled > changePitch');
          props.onHotkeyChangePitch?.(delta);
        },
        RAGE_HOTKEY_TIMEOUT_MS,
        { leading: true, trailing: false }
      ),
    [props.onHotkeyChangePitch]
  );

  const throttledSend = useMemo(
    () =>
      throttle(
        () => {
          verboseDebug(props, 'throttled > trySend');
          trySend(props.identifier, props.sendData);
        },
        RAGE_HOTKEY_TIMEOUT_MS,
        { leading: true, trailing: false }
      ),
    [trySend, props.identifier, props.sendData]
  );

  const handleDropball = useCallback(() => {
    const sent = dropball({
      source: props.identifier,
      checkActive: true,
      notifyResult: true,
    });

    if (!sent) {
      return;
    }

    // prevent additional drop ball attempts until we get a ball status update, or the cooldown elapses
    setFireState(FireState.Loading);
    setLoadingUntil(addSeconds(new Date(), DROP_BALL_COOLDOWN_SEC));
  }, [dropball, props.identifier, setFireState, setLoadingUntil]);

  const throttledDropBall = useMemo(
    () =>
      throttle(
        () => {
          verboseDebug(props, 'throttled > dropBall');
          handleDropball();
        },
        RAGE_HOTKEY_TIMEOUT_MS,
        { leading: true, trailing: false }
      ),
    [handleDropball]
  );

  const [delaying, setDelaying] = useState(false);
  const [delaySeconds, setDelaySeconds] = useState(0);

  const wasDroppingBall = usePrevious(droppingBall);

  // unlock button as soon as the drop ball resolves, rather than waiting out the cooldown
  useEffect(() => {
    if (!wasDroppingBall || droppingBall) {
      return;
    }

    setLoadingUntil(new Date());
  }, [droppingBall, wasDroppingBall]);

  // handles rage clicks to avoid sending multiple fires, drop balls, etc...
  const handleClick = useCallback(() => {
    verboseDebug(props, `handleClick > state > ${activeButton?.state}`);

    switch (activeButton?.state) {
      case FireState.DropBall: {
        handleDropball();
        return;
      }

      case FireState.UncalibratedOD: {
        NotifyHelper.error({
          message_md: 'Calibration logic is not setup yet!',
        });
        return;
      }

      case FireState.UntrainedBP: {
        if (!pitchBP) {
          console.warn({
            event: `${props.identifier}: abandoned train instruction (no pitchBP)`,
            state: activeButton?.state,
          });
          return;
        }

        if (!props.onTrainFn) {
          console.warn({
            event: `${props.identifier}: abandoned train instruction (no onTrainFn)`,
            state: activeButton?.state,
          });
          return;
        }

        props.onTrainFn([pitchBP._id]);
        return;
      }

      case FireState.LoadPitch: {
        // manually sending a pitch should require a manual fire before AF takes over again
        safeSetIgnoreAf(IgnoreAutoFireMode.Permanent);
        // immediately render as loading until next heartbeat
        setFireState(FireState.Loading);

        props.beforeSend?.();
        trySend(props.identifier, props.sendData);
        return;
      }

      case FireState.ReadyToManualFire: {
        const notReady = isTraining ? !pitchTraining : !pitchBP;

        if (notReady) {
          // if some final safety check fails, don't fire and notify the user
          console.warn({
            event: `${props.identifier}: abandoned fire instruction (no ${
              isTraining ? 'training pitch' : 'BP pitch'
            })`,
            state: activeButton?.state,
          });

          NotifyHelper.warning({
            message_md: 'The machine is not ready to fire.',
          });

          playSound(SfxName.NOT_READY);
          return;
        }

        // immediately render as firing until next heartbeat
        setFireState(FireState.NotReadyToFire);
        props.beforeFire?.('manual', true);
        tryFire(props.identifier, props.fireData).then((result) =>
          props.afterFire?.(result)
        );
        return;
      }

      case FireState.BallError: {
        openDialog(MachineDialogMode.Inspect);
        return;
      }

      default: {
        console.warn(
          `${props.identifier}: ignoring click from unexpected fire state ${activeButton?.state}`
        );
        return;
      }
    }
  }, [
    activeButton?.state,
    dropball,
    isTraining,
    props.beforeFire,
    props.afterFire,
    props.verbose,
    setAutoFire,
    tryFire,
    trySend,
    handleDropball,
  ]);

  const throttledClick = useMemo(
    () => throttle(() => handleClick(), RAGE_CLICK_TIMEOUT),
    [handleClick]
  );

  const latestTryFire = useLatest(tryFire);
  const latestBeforeFire = useLatest(props.beforeFire);

  // listen for when fire state enters auto-fire R2F => trigger fire
  useEffect(() => {
    if (!r2fReady) {
      // not ready yet
      return;
    }

    if (fireState !== FireState.ReadyToAutoFire) {
      // not supposed to auto-fire
      return;
    }

    verboseDebug(props, 'triggering auto-fire', r2fReady);
    latestBeforeFire.current?.('auto', true);
    latestTryFire.current(props.identifier, props.fireData);
  }, [fireState, r2fReady]);

  const handleDelayFire = useCallback(() => {
    verboseDebug(props, 'handleDelayFire');

    // don't play sound if delay is set to 0
    if (machine.remote_hotkeys?.delay_ms) {
      playSound(SfxName.DELAY_FIRE);
    }

    // setup the total time in seconds, allowing effict interval to wind it down
    setDelaySeconds(Math.ceil((machine.remote_hotkeys?.delay_ms ?? 0) / 1_000));
    setDelaying(true);
  }, [machine.remote_hotkeys?.delay_ms, playSound, setDelaySeconds]);

  // handles rage/accidental clicks to avoid starting multiple countdowns
  const throttledDelayFire = useMemo(
    () =>
      throttle(() => handleDelayFire(), RAGE_HOTKEY_TIMEOUT_MS, {
        leading: false,
        trailing: true,
      }),
    [handleDelayFire]
  );

  // while delaying fire, count down the timer every second
  useEffect(() => {
    if (!delaying) {
      return;
    }

    const timeout = setTimeout(() => {
      if (delaySeconds > 0) {
        verboseDebug(props, `delay countdown > ${delaySeconds}s remaining`);
        setDelaySeconds((prev) => prev - 1);
        return;
      }

      // clean up delay stuff so the label goes back to normal
      setDelaying(false);
      clearTimeout(timeout);

      // do the fire action (if possible)
      handleClick();

      return () => {
        clearTimeout(timeout);
      };
    }, 1_000);
  }, [delaying, delaySeconds]);

  // cancel any pending throttles when component unmounts
  useEffect(() => {
    return () => {
      throttledClick.cancel();
      throttledChangePitch.cancel();
      throttledDelayFire.cancel();
      throttledSend.cancel();
    };
  }, [throttledClick, throttledChangePitch, throttledDelayFire, throttledSend]);

  if (!calibrated || !canCalibrate) {
    return (
      <MachineCalibrateButton size={props.size} className={props.className} />
    );
  }

  switch (fireState) {
    case FireState.Busy:
    case FireState.OfflineUser:
    case FireState.OfflineMachine: {
      return (
        <MachineUnavailableButton
          size={props.size}
          className={props.className}
        />
      );
    }

    default: {
      const onCalibrationModel = !!activeModel?.calibration_only;

      // ball drop should take precendence over needing to run BTC
      const isDropBall = [FireState.DropBall, FireState.Loading].includes(
        activeButton?.state as FireState
      );

      const showCalibrateBallButton =
        props.isSidebarControl && onCalibrationModel && !isDropBall;

      // since never want to show this while we're firing in BTC, we'll only override the sidebar button
      if (showCalibrateBallButton) {
        return <CalibrateBallButton />;
      }

      if (!activeButton) {
        verboseDebug(
          props,
          `unexpected fire state > ${fireState} > no active button`
        );
        return null;
      }

      const label =
        delaying && !!machine.remote_hotkeys?.delay_ms
          ? `${t(activeButton.label)} in ${delaySeconds}s`
          : t(activeButton.label);

      // * FireState.DropBall is neither disabled nor spinning, so the lock has to gate the
      // * button directly instead of relying on the heartbeat reaching FireState.Loading
      const disabled = activeButton.disabled || droppingBall;
      const spinning = activeButton.spinning || droppingBall;

      const content =
        props.as === 'dialog-button' ? (
          <DialogButton
            className={props.className}
            color={activeButton.color}
            disabled={disabled}
            icon={spinning ? <Spinner /> : undefined}
            label={spinning ? undefined : label}
            onClick={throttledClick}
            variant={activeButton.variant}
          />
        ) : (
          <Button
            size={props.size}
            className={props.className}
            color={activeButton.color}
            disabled={disabled}
            onClick={throttledClick}
            variant={activeButton.variant}
          >
            {spinning && <Spinner />}
            <Text truncate>{label}</Text>
          </Button>
        );

      return (
        <ErrorBoundary identifier={props.identifier}>{content}</ErrorBoundary>
      );
    }
  }
};
